import React from "react";

interface FormInputProps {
  icon?: React.ReactNode;
  label: string;
  name: string;
  type?: string;
  value?: string;
  checked?: boolean;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  placeholder?: string;
  required?: boolean;
  options?: { value: string; label: string; disabled?: boolean }[];
  error?: string;
  className?: string;
  disabled?: boolean;
}

const FormInput: React.FC<FormInputProps> = ({
  icon,
  label,
  name,
  type = "text",
  value = "",
  checked,
  onChange,
  placeholder,
  required,
  options,
  error,
  className = "",
  disabled = false,
}) => {
  const inputClasses = `
    ${type === "checkbox" ? "h-4 w-4" : "w-full"} rounded${
    type === "checkbox" ? "" : "-lg"
  } 
    border border-gray-300 dark:border-gray-600 bw:border-black
    bg-white dark:bg-gray-700 bw:bg-white
    ${icon ? "pl-10" : type === "checkbox" ? "" : "pl-3"} ${
    type === "checkbox" ? "" : "pr-3 py-2"
  }
    text-gray-900 dark:text-white bw:text-black
    placeholder-gray-400 dark:placeholder-gray-500 bw:placeholder-gray-600
    focus:border-primary-500 dark:focus:border-primary-400 bw:focus:border-black
    focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bw:focus:ring-black
    disabled:bg-gray-100 dark:disabled:bg-gray-800 bw:disabled:bg-gray-100
    disabled:cursor-not-allowed
    transition-colors duration-200
    ${error ? "border-red-500 dark:border-red-400 bw:border-black" : ""}
    ${className}
  `;

  return (
    <div>
      {type === "checkbox" ? (
        <div className="flex items-center">
          <input
            type="checkbox"
            name={name}
            checked={checked}
            onChange={onChange}
            className={inputClasses}
            disabled={disabled}
          />
          <label className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300 bw:text-black">
            {label}
            {required && (
              <span className="text-red-500 dark:text-red-400 bw:text-black ml-1">
                *
              </span>
            )}
          </label>
        </div>
      ) : (
        <>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 bw:text-black mb-1">
            {label}
            {required && (
              <span className="text-red-500 dark:text-red-400 bw:text-black ml-1">
                *
              </span>
            )}
          </label>
          <div className="relative">
            {icon && (
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 dark:text-gray-500 bw:text-black">
                {icon}
              </div>
            )}
            {type === "select" ? (
              <select
                name={name}
                value={value}
                onChange={onChange}
                className={inputClasses}
                required={required}
                disabled={disabled}
              >
                <option value="">{placeholder || "Select an option"}</option>
                {options?.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                className={inputClasses}
                placeholder={placeholder}
                required={required}
                disabled={disabled}
              />
            )}
          </div>
        </>
      )}
      {error && (
        <p className="mt-1 text-sm text-red-500 dark:text-red-400 bw:text-black">
          {error}
        </p>
      )}
    </div>
  );
};

export default FormInput;
